import React from 'react';
import { Form, Formik, FormikValues } from 'formik';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { ObjectSchema } from 'yup';
import Button from '@components/shared/Button';
import DateInput from '@components/shared/Form/DateInput';
import Input from '@components/shared/Form/Input';
import Textarea from '@components/shared/Form/Textarea';
import { FieldType, FormField } from '@contracts';
import InputPhone from './Form/InputPhone';
import MultiCheckboxComponent from './Form/MultiCheckbox';
import SelectDropdown from './Form/SelectDropdown';

interface Props {
  initialValues: FormikValues;
  fields: FormField[];
  validationSchema: ObjectSchema<any>;
  onSubmit: (values: FormikValues) => void;
  submitButtonText?: string;
}

const FormContainer: React.FC<Props> = ({ initialValues, fields, validationSchema, onSubmit, submitButtonText }) => {
  const { t } = useTranslation();

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values) => onSubmit(values)}>
      {({ values, errors, handleChange, handleBlur, isValid, setFieldValue }) => {
        return (
          <Form>
            {outputFields(fields, values, errors, handleChange, handleBlur, setFieldValue)}
            <Button type="submit" disabled={!isValid}>
              {submitButtonText ?? t('components.submit')}
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
};

function getFieldId(field) {
  if (field.parent) {
    return `${field.parent}.${field.id}`;
  }
  return field.id;
}

function getFieldValue(field, values) {
  if (field.parent) {
    return values[field.parent] && values[field.parent][field.id];
  }
  return values[field.id];
}

export function outputFields(fields, values, errors, handleChange, handleBlur, setFieldValue) {
  return fields.map((field, i) => {
    const value = getFieldValue(field, values);

    switch (field.type) {
      case FieldType.Text:
        return (
          <Input
            key={i}
            className={field.class}
            name={getFieldId(field)}
            value={value}
            type="text"
            label={field.label}
            placeholder={field.placeholder}
            description={field.description}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        );
      case FieldType.Email:
        return (
          <Input
            key={i}
            className={field.class}
            name={getFieldId(field)}
            value={value}
            type="email"
            label={field.label}
            placeholder={field.placeholder}
            description={field.description}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        );
      case FieldType.Textarea:
        return (
          <Textarea
            key={i}
            name={getFieldId(field)}
            value={value}
            label={field.label}
            placeholder={field.placeholder}
            description={field.description}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        );
      case FieldType.Select:
        return (
          <SelectDropdown
            key={i}
            id={getFieldId(field)}
            options={field.options}
            label={field.label}
            onChange={(option) => setFieldValue(getFieldId(field), option.value)}
            onBlur={handleBlur}
            name={getFieldId(field)}
            value={value}
            placeholder={field.placeholder}
            description={field.description}
          />
        );
      case FieldType.Phone:
        return (
          <InputPhone
            key={i}
            id={getFieldId(field)}
            value={value}
            name={getFieldId(field)}
            label={field.label}
            onChange={(phoneWithoutPrefix) => setFieldValue(getFieldId(field), `+${phoneWithoutPrefix}`)}
            onBlur={handleBlur}
            required={field.required}
          />
        );
      case FieldType.MultiCheckbox:
        return (
          <MultiCheckboxComponent
            key={i}
            id={getFieldId(field)}
            values={values}
            label={field.label}
            options={field.options}
            design={field.config?.design}
          />
        );
      case FieldType.Date:
        return <DateInput key={i} id={getFieldId(field)} value={value} name={getFieldId(field)} label={field.label} onChange={handleChange} />;
      default:
        throw Error('Field type not configured');
    }
  });
}

export default FormContainer;
